import "./index.css";
import { clsx } from "clsx";
import { Trash2Icon, XIcon } from "lucide-react";
import type { MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { NotificationsRepository } from "@/api/notifications/repository";
import { NotificationsService } from "@/api/notifications/service";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { useNotificationsStore } from "@/store/notifications/slice";
import type { INotification } from "@/types/notifications";
import { IconButton, Text } from "@/ui-kit";
import { formatNotificationDate } from "../../../Notifications/utils";

type IProps = {
  item: INotification;
};

export const Notification = ({ item }: IProps) => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();

  const repository = useMemo(() => {
    return new NotificationsRepository(new NotificationsService(), {
      abort: true,
    });
  }, []);

  const handleRead = useCallback(() => {
    if (!item.isRead) {
      useNotificationsStore.getState().readNotification(item);
      repository.markAsRead({
        id: item.id,
      });
    }
    useNotificationsStore.getState().setOpen(false);
    const pathname = item.link.split(".com/")[1];
    navigate(`/${pathname}`);
  }, [item, navigate, repository]);

  const handleArchive = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      useNotificationsStore.getState().archiveNotification(item);
      repository.delete({
        id: item.id,
      });
    },
    [repository, item],
  );

  return (
    <div className="notification" onClick={handleRead}>
      <div
        className={clsx(
          "notification-source__marker nuco-color-brand",
          item.isRead && "notification-source__marker--read",
        )}
      />
      <div className="notification-details">
        <header className="notification-details__header">
          {item.title && (
            <Text
              size={isMobile ? "lg" : "md"}
              weight="bold"
              as="p"
              className="notification-title"
            >
              {item.title}
            </Text>
          )}
          <div className="notification-actions">
            <IconButton
              size="sm"
              color="neutral"
              variant={isMobile ? "ghost" : "soft"}
              onClick={handleArchive}
            >
              {isMobile ? <Trash2Icon /> : <XIcon />}
            </IconButton>
          </div>
        </header>
        {item.message && (
          <Text
            size={isMobile ? "lg" : "md"}
            as="p"
            className="notification-text"
          >
            {item.message}
          </Text>
        )}
        <div className="notification-time">
          <Text size={isMobile ? "lg" : "md"}>
            {formatNotificationDate(item.createdOn)}
          </Text>
        </div>
      </div>
    </div>
  );
};
